import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CTable,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CButton,
  CAlert,
  CRow,
  CCol,
  CBadge,
  CCollapse,
} from '@coreui/react';
import Navigation from '../components/Navigation';
import useApiDocViewModel from '../viewmodels/ApiDocViewModel';

const ApiTitle = () => {
  return (
    <CCard className="border-0 shadow-sm">
      <CCardBody>
        <h4 className="mb-3">SDK Dashboard API Documentation</h4>
        <CAlert color="info" className="d-flex align-items-center mb-0">
          <div>
            <p className="mb-2">
              <strong>Getting Started:</strong>
            </p>
            <ol className="mb-0">
              <li className="mb-2">Apply for an APP key using the button below</li>
              <li className="mb-2">Use the APP key to authenticate and get a session ID</li>
              <li className="mb-0">Use the session ID to access other API endpoints</li>
            </ol>
          </div>
        </CAlert>
      </CCardBody>
    </CCard>
  );
};

const AppKeyApplyView = ({ handleRequestApiKey }) => {
  return (
    <CCard className="border-0 shadow-sm">
      <CCardBody>
        <h5 className="mb-3">Get Your APP Key</h5>
        <div className="mb-3">
          When you apply for an APP Key, the new key will be sent to your email address.
          <strong className="text-warning"> Note: </strong>
          Any previously issued APP keys will be invalidated.
        </div>
        <CButton
          color="primary"
          size="lg"
          className="px-4"
          onClick={handleRequestApiKey}
        >
          Apply for an APP Key
        </CButton>
      </CCardBody>
    </CCard>
  );
};

const ApiInfoTable = ({ title, data, items }) => {
  if (!Array.isArray(data)) return null;
  if (!Array.isArray(items)) return null;
  if (items.length !== 3) return null;

  return (
    <>
      <CTableRow>
        <CTableHeaderCell colSpan={3}>
          <div className="d-flex align-items-center py-2">
            <strong>{title}</strong>
          </div>
        </CTableHeaderCell>
      </CTableRow>
      <CTableRow>
        <CTableHeaderCell className="text-muted">{items[0].label}</CTableHeaderCell>
        <CTableHeaderCell className="text-muted">{items[1].label}</CTableHeaderCell>
        <CTableHeaderCell className="text-muted">{items[2].label}</CTableHeaderCell>
      </CTableRow>
      {data.map((value, index) => (
        <CTableRow key={index} className="border-bottom">
          <CTableDataCell className={`font-monospace ${items[0].testClass}`}>{value[items[0].key]}</CTableDataCell>
          <CTableDataCell className={`font-monospace ${items[1].testClass}`}>{value[items[1].key]}</CTableDataCell>
          <CTableDataCell>{value[items[2].key]}</CTableDataCell>
        </CTableRow>
      ))}
    </>
  );
};

const ApiDocElement = ({ api_doc }) => {
  const [visible, setVisible] = React.useState(false);
  const paramItems = useMemo(() => {
    return [
      { label: "Key", key: "key", testClass: "text-primary" },
      { label: "Type", key: "type", testClass: "text-success" },
      { label: "Description", key: "description" }
    ];
  }, []);
  const headerItems = useMemo(() => {
    return [
      { label: "Key", key: "key", testClass: "text-primary" },
      { label: "Value", key: "value", testClass: "text-success" },
      { label: "Description", key: "description" }
    ];
  }, []);
  const errorItems = useMemo(() => {
    return [
      { label: "Status", key: "status", testClass: "text-danger" },
      { label: "Message", key: "message", testClass: "text-warning" },
      { label: "Description", key: "description" }
    ];
  }, []);

  const getMethodBadgeColor = (method) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'success';
      case 'POST': return 'primary';
      case 'PUT': return 'warning';
      case 'DELETE': return 'danger';
      default: return 'info';
    }
  };

  return (
    <CCard className="shadow-sm mb-4 border-0">
      <CCardHeader 
        className="border-bottom cursor-pointer" 
        onClick={() => setVisible(!visible)}
        style={{ cursor: 'pointer' }}
      >
        <CRow className="d-flex align-items-center">
          <CCol md={1} className='mt-1 ms-2'>
            <CBadge color={getMethodBadgeColor(api_doc.method)} className="text-uppercase px-3 py-2">
              {api_doc.method}
            </CBadge>
          </CCol>
          <CCol>
            <div className="p-2 font-monospace">
              {api_doc.url}
            </div>
          </CCol>
          <CCol md={1} className="text-end">
            <FontAwesomeIcon icon={visible ? faChevronUp : faChevronDown} />
          </CCol>
        </CRow>
      </CCardHeader>
      <CCollapse visible={visible}>
        <CCardBody className="p-0">
          <CTable bordered responsive className="mb-0">
            <CTableBody>
              <CTableRow>
                <CTableHeaderCell style={{ width: '120px' }}>
                  <strong>Description</strong>
                </CTableHeaderCell>
                <CTableDataCell colSpan={2}>
                  {api_doc.description}
                </CTableDataCell>
              </CTableRow>

              <ApiInfoTable title={"Request Query Parameters"} data={api_doc.request.query} items={paramItems} />
              <ApiInfoTable title={"Request Headers"} data={api_doc.request.header} items={headerItems} />
              <ApiInfoTable title={"Request Body Parameters"} data={api_doc.request.body} items={paramItems} />
              <ApiInfoTable title={"Response Body Parameters"} data={api_doc.response.body} items={paramItems} />

              <CTableRow>
                <CTableHeaderCell className="" colSpan={3}>
                  <strong>Example</strong>
                </CTableHeaderCell>
              </CTableRow>
              <CTableRow>
                <CTableHeaderCell className="align-top" style={{ width: '120px' }}>
                  Request
                </CTableHeaderCell>
                <CTableDataCell className="font-monospace" colSpan={2}>
                  <div className="bg-light p-3 rounded">
                    {api_doc.request.example.uri && (
                      <div className="mb-2">
                        <div className="text-muted small mb-1">URL:</div>
                        <code>{api_doc.request.example.uri}</code>
                      </div>
                    )}
                    {api_doc.request.example.header && (
                      <div className="mb-2">
                        <div className="text-muted small mb-1">Headers:</div>
                        <code>{api_doc.request.example.header}</code>
                      </div>
                    )}
                    {api_doc.request.example.body && (
                      <div>
                        <div className="text-muted small mb-1">Body:</div>
                        <code>{JSON.stringify(api_doc.request.example.body, null, 2)}</code>
                      </div>
                    )}
                  </div>
                </CTableDataCell>
              </CTableRow>
              <CTableRow>
                <CTableHeaderCell className="align-top" style={{ width: '120px' }}>
                  Response
                </CTableHeaderCell>
                <CTableDataCell className="font-monospace" colSpan={2}>
                  <div className="p-3 rounded bg-light">
                    <pre className="mb-0"><code>{JSON.stringify(api_doc.response.example, null, 2)}</code></pre>
                  </div>
                </CTableDataCell>
              </CTableRow>
              
              {api_doc.response.error && (
                <ApiInfoTable title={"Error Responses"} data={api_doc.response.error} items={errorItems} />
              )}
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
};

const ApiDocView = () => {
  const { apiInfo, handleRequestApiKey } = useApiDocViewModel();

  return (
    <div className="bg-light min-vh-100">
      <Navigation />
      <CContainer className="py-4">
        <CRow className="mb-4">
          <CCol>
            <ApiTitle />
          </CCol>
        </CRow>

        <CRow className="mb-4">
          <CCol>
            <AppKeyApplyView handleRequestApiKey={handleRequestApiKey} />
          </CCol>
        </CRow>

        <br />
        <h4 className="mt-2 mb-4">API Endpoints</h4>
        {apiInfo.map((api_doc, index) => (
          <ApiDocElement key={index} api_doc={api_doc} />
        ))}
      </CContainer>
    </div>
  );
};

export default ApiDocView;